// Single source of truth for every piece of content on the site.
// Sourced from the LinkedIn CV export — update here when the CV changes.

export const profile = {
  name: 'Sudip Shrestha',
  initials: 'S.S',
  headline: 'Big Data & Platform Engineer | AI',
  role: 'Senior Data & Platform Engineer, Lead',
  company: 'Dlytica Inc.',
  location: 'Lalitpur, Bāgmatī, Nepal',
  tagline:
    'I design and ship intelligent big data platforms — from on-premises Kubernetes and OpenShift deployments to cloud-native pipelines on AWS and Azure.',
  summary:
    'I am an experienced big data engineer and architect designing intelligent big data platforms, with expertise in designing and optimizing data pipelines, data modeling, and end-to-end workflows. Proficient in cloud platforms like AWS and Azure, I specialize in data warehousing, modeling, and scalable deployment solutions. I have extensive experience implementing on-premises big data solutions using tools such as Kafka, Airbyte, NiFi, and Superset, and deploying them on Kubernetes and OpenShift environments. My skills also extend to integrating data platforms with user-facing applications, leveraging web development expertise. Additionally, I have a strong understanding of AI and machine learning, applying data-driven solutions to complex challenges across various domains.',
  facts: [
    { label: 'Experience', value: '3+ years' },
    { label: 'Based in', value: 'Lalitpur, Nepal' },
    { label: 'Focus', value: 'Big data platforms' },
    { label: 'Also into', value: 'AI & machine learning' },
  ],
};

export const contact = {
  email: 'sudips413@gmail.com',
  phone: '9860817106',
  website: 'https://sudip-shrestha.com.np/',
  blog: 'https://blog-sudips413.vercel.app/',
};

export const socials = [
  { name: 'LinkedIn', url: 'https://www.linkedin.com/in/sudipshrestha413/', icon: 'linkedin' },
  { name: 'GitHub', url: 'https://github.com/sudips413', icon: 'github' },
  { name: 'Blog', url: 'https://blog-sudips413.vercel.app/', icon: 'article' },
  { name: 'Twitter', url: 'https://twitter.com/Stha_sud', icon: 'twitter' },
  { name: 'Facebook', url: 'https://www.facebook.com/thepprincce', icon: 'facebook' },
  { name: 'Instagram', url: 'https://www.instagram.com/me_sudip_shrestha/', icon: 'instagram' },
];

export const experience = [
  {
    company: 'Dlytica Inc.',
    duration: '2 yrs 10 mos',
    roles: [
      {
        title: 'Senior Data & Platform Engineer, Lead',
        period: 'Jan 2026 – Present',
        location: 'Canada | Nepal',
        current: true,
        points: [
          'Implementing DataNature, an intelligent big data platform, for on-premises and cloud deployments across South Asia, North America and the Middle East.',
          'Engaging with clients to deliver tailored solutions and ensure project success.',
          'Leading end-to-end project execution with a focus on planning, delivery and team coordination.',
          'Contributing to data architecture design for scalable solutions.',
        ],
      },
      {
        title: 'Data Architect & Engineer',
        period: 'Dec 2024 – Jan 2026',
        location: 'Nepal',
        points: [
          'Designed a reconciliation pipeline on CDH and CDP (Cloudera Hadoop based big data platforms).',
          'Managed the ELT pipeline for telecom data on Cloudera Data Platform.',
          'Worked on an Informatica Cloud and PowerCenter 10 proof of concept.',
          'Led end-to-end project execution across planning, delivery and team coordination.',
        ],
      },
      {
        title: 'Data Engineer',
        period: 'Oct 2023 – Dec 2024',
        location: 'Canada',
        points: [
          'Designed and maintained data pipelines, monitoring, reconciliation and auditing processes on Cloudera.',
          'Built a Customer 360 platform for the banking sector and shipped it for Nepali banking.',
          'Delivered big data platforms for leading telecommunications organizations such as Ooredoo.',
          'Contributed to Azure data pipeline development using Azure Data Factory.',
          'Played a key role in a generative AI project focused on forensic lab data digitalization.',
          'Designed DataNature on-premises on both Kubernetes and OpenShift.',
        ],
      },
      {
        title: 'Data Engineer Tutor',
        period: 'Jan 2024 – May 2024',
        location: 'Canada',
        points: [
          'Tutored the foundations of Python, SQL, ETL & ELT pipelines, EDA and machine learning.',
          'Provided training on data engineering tool installation on Kubernetes, guiding teams through open-source DE platforms.',
        ],
      },
    ],
  },
  {
    company: 'Genese Solution',
    roles: [
      {
        title: 'Data Engineer',
        period: 'May 2023 – Oct 2023',
        location: 'Lalitpur',
        points: [
          'Developed and implemented data pipelines using Azure Synapse Analytics for a commercial site.',
          'Automated data entry processes through custom solutions in WordPress Admin.',
          'Contributed to health sector projects, including the creation of HL7 messages for seamless healthcare data exchange.',
        ],
      },
    ],
  },
  {
    company: 'Code Rush',
    roles: [
      {
        title: 'Data Engineer',
        period: 'Nov 2022 – May 2023',
        location: 'Bhanimandal, Lalitpur',
        points: [
          'Specialized in data engineering with expertise in DE tooling and AWS services for data solutions.',
          'Delivered end-to-end data engineering and analytics projects, ensuring efficient data workflows and insights.',
          'Implemented complete data engineering solutions using AWS Glue, Athena, Kinesis and Redshift.',
        ],
      },
    ],
  },
  {
    company: 'Code for Nepal',
    roles: [
      {
        title: 'Data Fellow',
        period: 'Jan 2023 – Mar 2023',
        location: 'Kathmandu, Bāgmatī, Nepal',
        points: [
          'Studied Python and SQL, and completed the data analytics and data engineering paths from DataCamp.',
        ],
      },
    ],
  },
];

// Milestones drawn along the rail in the welcome animation.
export const journey = [
  { year: '2022', place: 'Code Rush', role: 'Data Engineer' },
  { year: '2023', place: 'Genese Solution', role: 'Data Engineer' },
  { year: '2023', place: 'Dlytica Inc.', role: 'Data Engineer' },
  { year: '2024', place: 'Dlytica Inc.', role: 'Data Architect' },
  { year: '2026', place: 'Dlytica Inc.', role: 'Senior Engineer, Lead' },
];

export const skills = [
  {
    group: 'Big Data & Streaming',
    items: ['Kafka', 'Apache NiFi', 'Airbyte', 'Superset', 'Cloudera CDH / CDP', 'Hadoop'],
  },
  {
    group: 'Cloud',
    items: ['AWS Glue', 'Athena', 'Kinesis', 'Redshift', 'Azure Data Factory', 'Azure Synapse Analytics'],
  },
  {
    group: 'Platform & DevOps',
    items: ['Kubernetes', 'OpenShift', 'MinIO', 'Open-source platform engineering'],
  },
  {
    group: 'Data Engineering',
    items: ['ETL / ELT', 'Data modeling', 'Data warehousing', 'Reconciliation & auditing', 'Informatica'],
  },
  {
    group: 'Languages & AI',
    items: ['Python', 'SQL', 'Machine learning', 'Generative AI'],
  },
  {
    group: 'Web',
    items: ['React', 'JavaScript', 'WordPress'],
  },
];

export const projects = [
  {
    name: 'DataNature',
    subtitle: 'Intelligent big data platform',
    description:
      'An intelligent big data platform delivered for on-premises and cloud environments across South Asia, North America and the Middle East — designed to run on both Kubernetes and OpenShift.',
    tags: ['Kubernetes', 'OpenShift', 'Kafka', 'NiFi', 'Superset'],
  },
  {
    name: 'Customer 360',
    subtitle: 'Banking sector data platform',
    description:
      'A Customer 360 platform tailored for the banking sector, built on Cloudera and successfully implemented for Nepali banking.',
    tags: ['Cloudera', 'Data modeling', 'Banking'],
  },
  {
    name: 'Telecom Big Data Platform',
    subtitle: 'Ooredoo and telecom clients',
    description:
      'Big data platforms delivered for leading telecommunications organizations, including the ELT pipeline for telecom data running on Cloudera Data Platform.',
    tags: ['CDP', 'ELT', 'Telecom'],
  },
  {
    name: 'DocuContext',
    subtitle: 'Intelligent document processing',
    description:
      'Intelligent document processing powered by generative AI, turning unstructured documents into structured, queryable data.',
    tags: ['Generative AI', 'IDP', 'Python'],
  },
  {
    name: 'Forensic Lab Digitalization',
    subtitle: 'Generative AI project',
    description:
      'A generative AI project focused on digitalizing forensic laboratory data, replacing manual record handling with an automated pipeline.',
    tags: ['Generative AI', 'Data pipelines'],
  },
  {
    name: 'Reconciliation & Audit Pipeline',
    subtitle: 'Cloudera CDH / CDP',
    description:
      'A reconciliation pipeline on CDH and CDP with monitoring and auditing processes, keeping large-scale data movement verifiable end to end.',
    tags: ['Cloudera', 'Monitoring', 'Auditing'],
  },
];

// The personal side of the site. Reword any of these to match your own voice —
// nothing here comes from the CV.
export const interests = [
  {
    name: 'Travel & Trekking',
    icon: 'hiking',
    description:
      'Trails, ridgelines and small towns — mostly around Nepal, and anywhere else I can get to. Every trip ends up as notes and photos, and the better ones become posts on my blog.',
  },
  {
    name: 'Photography',
    icon: 'camera',
    description:
      'A camera comes along on every trip. Mountains, streets and the people in them — I like the ordinary frames as much as the postcard ones.',
  },
  {
    name: 'Music',
    icon: 'music',
    description:
      'Constant company on long build days and longer bus rides. It is the one thing that runs in the background of everything else I do.',
  },
];

export const blog = {
  name: 'KP — Knowledge Portals',
  url: 'https://blog-sudips413.vercel.app/',
  description:
    'Where the travel writing lives, alongside notes on data engineering and whatever else is worth sharing. Post, discover and connect with a global community.',
};

export const education = [
  {
    school: 'Khwopa College of Engineering',
    qualification: "Bachelor's degree, Computer Engineering",
    period: 'Jan 2018 – May 2023',
  },
  {
    school: "St. Xavier's College, Maitighar",
    qualification: 'NEB, Science',
    period: '2016 – 2018',
  },
  {
    school: 'Deepika Higher Secondary School',
    qualification: 'SLC',
    period: '2006 – 2016',
  },
];

export const sections = [
  { id: 'about', label: 'About' },
  { id: 'experience', label: 'Experience' },
  { id: 'skills', label: 'Skills' },
  { id: 'projects', label: 'Projects' },
  { id: 'education', label: 'Education' },
  { id: 'beyond', label: 'Beyond Work' },
  { id: 'contact', label: 'Contact' },
];
